import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/modal.css';
import axiosInstance from '../../api/axiosInstance';

const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];

// Each key of the `steps` payload object is an independent list with its own
// add/remove controls — they are not row-aligned with one another.
const STEP_LISTS = [
  {
    key: 'step_description',
    title: 'Step Descriptions',
    required: true,
    multiline: true,
    accent: 'blue',
    itemLabel: 'Step',
    placeholder: 'Describe the action to perform',
    addLabel: 'Add Step',
    icon: (
      <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    ),
  },
  {
    key: 'expected_result',
    title: 'Expected Results',
    required: true,
    multiline: true,
    accent: 'green',
    itemLabel: 'Result',
    placeholder: 'What should happen',
    addLabel: 'Add Result',
    icon: <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3" />,
  },
  {
    key: 'prerequisites',
    title: 'Prerequisites',
    required: true,
    multiline: false,
    accent: 'amber',
    itemLabel: 'Prerequisite',
    placeholder: 'e.g. User is on the login page',
    addLabel: 'Add Prerequisite',
    icon: (
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    ),
  },
  {
    key: 'additional_notes',
    title: 'Additional Notes',
    required: false,
    multiline: false,
    accent: 'slate',
    itemLabel: 'Note',
    placeholder: 'Any extra context for the tester',
    addLabel: 'Add Note',
    icon: (
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5" />
    ),
  },
];

const createEmptyLists = () =>
  STEP_LISTS.reduce((acc, field) => ({ ...acc, [field.key]: [''] }), {});

const CreateTestCaseModal = ({
  isOpen,
  onClose,
  onCreateTestCase,
  testSuiteId,
  projectId,
  projectName,
}) => {
  const [testCaseName, setTestCaseName] = useState('');
  const [testCaseDescription, setTestCaseDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [storyId, setStoryId] = useState('');
  const [environmentId, setEnvironmentId] = useState('');
  const [stepLists, setStepLists] = useState(createEmptyLists);

  const [environments, setEnvironments] = useState([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);
  const [stories, setStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(false);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const resetForm = useCallback(() => {
    setTestCaseName('');
    setTestCaseDescription('');
    setPriority('Medium');
    setStoryId('');
    setEnvironmentId('');
    setStepLists(createEmptyLists());
    setError(null);
    setLoading(false);
  }, []);

  const fetchEnvironments = useCallback(async (envProjectId) => {
    if (!envProjectId) {
      setEnvironments([]);
      return;
    }

    try {
      setEnvironmentsLoading(true);
      const response = await axiosInstance.get(
        `/user_profile/list_environments?project_id=${envProjectId}`
      );

      if (response?.status_code === 200) {
        setEnvironments(response?.data || []);
      } else {
        console.error('Failed to fetch environments:', response);
        setEnvironments([]);
      }
    } catch (err) {
      console.error('Error fetching environments:', err);
      setEnvironments([]);
    } finally {
      setEnvironmentsLoading(false);
    }
  }, []);

  const fetchStories = useCallback(async (storyProjectId) => {
    if (!storyProjectId) {
      setStories([]);
      return;
    }

    try {
      setStoriesLoading(true);
      const response = await axiosInstance.get(
        `/jira_data/story_dropdown?project_id=${storyProjectId}`
      );

      if (response?.status === 'success' && Array.isArray(response?.stories)) {
        setStories(response.stories);
      } else {
        setStories([]);
      }
    } catch (err) {
      console.error('Error fetching project stories:', err);
      setStories([]);
    } finally {
      setStoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    fetchEnvironments(projectId);
    fetchStories(projectId);
  }, [isOpen, projectId, fetchEnvironments, fetchStories, resetForm]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !loading) onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  const handleItemChange = (key, index, value) => {
    setStepLists((prev) => ({
      ...prev,
      [key]: prev[key].map((item, i) => (i === index ? value : item)),
    }));
  };

  const handleAddItem = (key) => {
    setStepLists((prev) => ({ ...prev, [key]: [...prev[key], ''] }));
  };

  const handleRemoveItem = (key, index) => {
    setStepLists((prev) => ({
      ...prev,
      [key]:
        prev[key].length === 1
          ? ['']
          : prev[key].filter((_, i) => i !== index),
    }));
  };

  const getCleanList = (key) =>
    (stepLists[key] || []).map((item) => item.trim()).filter(Boolean);

  const validate = () => {
    if (!projectId) return 'Project is required. Please select a project first.';
    if (!testCaseName.trim()) return 'Test case name is required';
    if (!priority) return 'Priority is required';
    if (!environmentId) return 'Environment is required';

    const missing = STEP_LISTS.filter(
      (field) => field.required && getCleanList(field.key).length === 0
    );

    if (missing.length) {
      return `Add at least one entry under: ${missing
        .map((field) => field.title)
        .join(', ')}`;
    }

    return null;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      project_id: parseInt(projectId, 10),
      name: testCaseName.trim(),
      priority,
      environment_id: parseInt(environmentId, 10),
      steps: STEP_LISTS.reduce(
        (acc, field) => ({ ...acc, [field.key]: getCleanList(field.key) }),
        {}
      ),
    };

    if (testCaseDescription.trim()) {
      payload.description = testCaseDescription.trim();
    }

    if (storyId) {
      payload.story_id = parseInt(storyId, 10);
    }

    try {
      const response = await axiosInstance.post(
        `/test_case/create_manual_test_case`,
        payload
      );

      if (response?.status_code === 201 || response?.status_code === 200) {
        const selectedEnvironment = environments.find(
          (env) => String(env.id) === String(environmentId)
        );

        onCreateTestCase({
          id: response?.test_case_id || Date.now(),
          name: payload.name,
          description: payload.description || '',
          priority: priority.toLowerCase(),
          status: 'pending',
          steps: payload.steps,
          environment: selectedEnvironment?.environment_name || '',
          environment_id: payload.environment_id,
          story_id: payload.story_id || null,
          suite_id: testSuiteId ? parseInt(testSuiteId, 10) : null,
          project_id: payload.project_id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        resetForm();
        onClose();
      } else {
        setError(response?.message || 'Failed to create test case. Please try again.');
      }
    } catch (err) {
      console.error('Error creating test case:', err);
      const data = err?.response?.data;

      if (err?.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (Array.isArray(data?.detail)) {
        const messages = data.detail.map(
          (item) => `${(item.loc || []).join('.')}: ${item.msg}`
        );
        setError(`Validation errors: ${messages.join(', ')}`);
      } else if (data?.detail || data?.message) {
        setError(data.detail || data.message);
      } else {
        setError('Failed to create test case. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      data-rt-modal-overlay
      className="create-test-case-modal-overlay"
      onClick={() => !loading && onClose()}
    >
      <div
        className="create-test-case-modal-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2>Create Test Case</h2>
            <p className="ctc-header-subtitle">
              Fill in the details below to add a manual test case
            </p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <form className="create-test-case-form" onSubmit={handleSubmit}>
            <section className="ctc-section">
              <div className="ctc-section-head">
                <h3 className="ctc-section-title">Basic Details</h3>
                {projectName && (
                  <span className="ctc-project-chip" title={projectName}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    </svg>
                    {projectName}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="test-case-name">
                  Test Case Name <span className="ctc-required">*</span>
                </label>
                <input
                  type="text"
                  id="test-case-name"
                  placeholder="e.g. Verify user can log in with valid credentials"
                  className="form-input"
                  value={testCaseName}
                  onChange={(e) => setTestCaseName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="test-case-description">
                  Description <span className="ctc-optional">(optional)</span>
                </label>
                <textarea
                  id="test-case-description"
                  placeholder="Brief summary of what this test case validates"
                  className="form-input"
                  rows="2"
                  value={testCaseDescription}
                  onChange={(e) => setTestCaseDescription(e.target.value)}
                ></textarea>
              </div>

              <div className="ctc-grid">
                <div className="form-group">
                  <label htmlFor="test-case-priority">
                    Priority <span className="ctc-required">*</span>
                  </label>
                  <select
                    id="test-case-priority"
                    className="form-input"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="test-case-environment">
                    Environment <span className="ctc-required">*</span>
                  </label>
                  <select
                    id="test-case-environment"
                    className="form-input"
                    value={environmentId}
                    onChange={(e) => setEnvironmentId(e.target.value)}
                    disabled={environmentsLoading || !projectId}
                  >
                    <option value="">
                      {environmentsLoading
                        ? 'Loading environments...'
                        : environments.length
                        ? 'Select environment'
                        : 'No environments available'}
                    </option>
                    {environments.map((env) => (
                      <option key={env.id} value={env.id}>
                        {env.environment_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="test-case-story">
                    Story <span className="ctc-optional">(optional)</span>
                  </label>
                  <select
                    id="test-case-story"
                    className="form-input"
                    value={storyId}
                    onChange={(e) => setStoryId(e.target.value)}
                    disabled={storiesLoading || !projectId}
                  >
                    <option value="">
                      {storiesLoading
                        ? 'Loading stories...'
                        : stories.length
                        ? 'Select story'
                        : 'No stories available'}
                    </option>
                    {stories.map((story) => (
                      <option key={story.id} value={story.id}>
                        {story.story_key}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="ctc-section ctc-steps-section">
              <div className="ctc-section-head">
                <h3 className="ctc-section-title">Test Steps</h3>
                <span className="ctc-section-hint">
                  Each list is managed separately
                </span>
              </div>

              <div className="ctc-lists">
                {STEP_LISTS.map((field) => {
                  const items = stepLists[field.key] || [];
                  const filledCount = getCleanList(field.key).length;

                  return (
                    <div
                      className={`ctc-list-card ctc-accent-${field.accent}`}
                      key={field.key}
                    >
                      <div className="ctc-list-head">
                        <div className="ctc-list-heading">
                          <span className="ctc-list-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              {field.icon}
                            </svg>
                          </span>
                          <span className="ctc-list-title">
                            {field.title}
                            {field.required ? (
                              <span className="ctc-required"> *</span>
                            ) : (
                              <span className="ctc-optional"> (optional)</span>
                            )}
                          </span>
                          <span className="ctc-list-count">{filledCount}</span>
                        </div>

                        <button
                          type="button"
                          className="ctc-add-btn"
                          onClick={() => handleAddItem(field.key)}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          {field.addLabel}
                        </button>
                      </div>

                      <div className="ctc-list-items">
                        {items.map((item, index) => (
                          <div className="ctc-list-row" key={`${field.key}-${index}`}>
                            <span
                              className="ctc-row-index"
                              title={`${field.itemLabel} ${index + 1}`}
                            >
                              {index + 1}
                            </span>

                            {field.multiline ? (
                              <textarea
                                className="form-input ctc-row-input"
                                rows="2"
                                placeholder={`${field.placeholder}`}
                                value={item}
                                aria-label={`${field.itemLabel} ${index + 1}`}
                                onChange={(e) =>
                                  handleItemChange(field.key, index, e.target.value)
                                }
                              ></textarea>
                            ) : (
                              <input
                                type="text"
                                className="form-input ctc-row-input"
                                placeholder={`${field.placeholder}`}
                                value={item}
                                aria-label={`${field.itemLabel} ${index + 1}`}
                                onChange={(e) =>
                                  handleItemChange(field.key, index, e.target.value)
                                }
                              />
                            )}

                            <button
                              type="button"
                              className="ctc-remove-btn"
                              onClick={() => handleRemoveItem(field.key, index)}
                              disabled={items.length === 1 && !item}
                              title={`Remove ${field.itemLabel.toLowerCase()} ${index + 1}`}
                              aria-label={`Remove ${field.itemLabel} ${index + 1}`}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0-.8 13a2 2 0 0 1-2 1.9H7.8a2 2 0 0 1-2-1.9L5 6" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {error && <div className="form-error ctc-error">{error}</div>}
          </form>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !projectId}
          >
            {loading ? 'Creating...' : 'Create Test Case'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateTestCaseModal;
